<template>
  <v-dialog
    v-model="dialog"
    max-width="600px"
    persistent
    :fullscreen="isRedirecting"
  >
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon left :color="statusIcon.color">{{ statusIcon.icon }}</v-icon>
        {{ $t('payment-method-ui.authenticating-payment') }}
      </v-card-title>

      <v-card-text>
        <!-- Loading state -->
        <div v-if="loading" class="text-center py-8">
          <v-progress-circular
            indeterminate
            size="64"
            color="primary"
            class="mb-4"
          ></v-progress-circular>
          <div class="text-h6 mb-2">{{ loadingMessage }}</div>
          <div class="text-caption text--secondary">
            {{ $t('payment-method-ui.do-not-close-window') }}
          </div>
        </div>

        <!-- Success state -->
        <div v-else-if="success" class="text-center py-8">
          <v-icon size="64" color="success" class="mb-4">mdi-check-circle</v-icon>
          <div class="text-h6 mb-2">{{ $t('payment-method-ui.authentication-success') }}</div>
          <div v-if="paymentIntent" class="mt-4">
            <v-chip small :color="statusColor(paymentIntent.status)" dark>
              {{ paymentIntent.status }}
            </v-chip>
          </div>
        </div>

        <!-- Error state -->
        <div v-else-if="error" class="text-center py-8">
          <v-icon size="64" color="error" class="mb-4">mdi-alert-circle</v-icon>
          <div class="text-h6 mb-2">{{ $t('payment-method-ui.authentication-failed') }}</div>
          <v-alert type="error" outlined class="mt-4">
            {{ errorMessage }}
          </v-alert>
        </div>

        <!-- Initial state: Authentication required -->
        <div v-else class="py-4">
          <v-alert type="warning" outlined prominent class="mb-4">
            <div class="d-flex align-center">
              <v-icon left large>mdi-shield-lock-outline</v-icon>
              <div>
                <div class="text-h6 mb-1">{{ $t('payment-method-ui.authentication-required') }}</div>
                <div class="text-body-2">{{ $t('payment-method-ui.authentication-required-message') }}</div>
              </div>
            </div>
          </v-alert>

          <!-- Payment details -->
          <v-simple-table dense v-if="paymentResponse" class="mb-4">
            <template v-slot:default>
              <tbody>
                <tr>
                  <td><strong>{{ $t('payment-method-ui.transaction-id') }}:</strong></td>
                  <td>{{ paymentResponse.id }}</td>
                </tr>
                <tr>
                  <td><strong>{{ $t('amount') }}:</strong></td>
                  <td>{{ formatAmount(paymentResponse.amount, paymentResponse.currency) }}</td>
                </tr>
                <tr>
                  <td><strong>{{ $t('status') }}:</strong></td>
                  <td>
                    <v-chip x-small :color="statusColor(paymentResponse.status)" dark>
                      {{ paymentResponse.status }}
                    </v-chip>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <!-- Authentication link (if available) -->
          <v-alert
            v-if="authenticationUrl"
            type="info"
            outlined
            dense
            class="mb-4"
          >
            <div class="text-caption mb-2"><strong>{{ $t('payment-method-ui.authentication-link') }}:</strong></div>
            <div class="d-flex align-center">
              <v-text-field
                :value="authenticationUrl"
                readonly
                dense
                outlined
                hide-details
                class="mr-2"
              ></v-text-field>
              <v-btn
                small
                icon
                @click="copyAuthLink"
                :title="$t('payment-method-ui.copy-link')"
              >
                <v-icon small>{{ linkCopied ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
              </v-btn>
            </div>
          </v-alert>
        </div>
      </v-card-text>

      <v-card-actions v-if="!loading">
        <v-spacer></v-spacer>

        <!-- Initial state actions -->
        <template v-if="!success && !error && !loading">
          <v-btn
              small
            text
            @click="close"
          >
            {{ $t('cancel') }}
          </v-btn>
          <v-btn
              small
            outlined
            color="secondary"
            @click="copyAuthLink"
          >
            <v-icon left small>{{ linkCopied ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
            {{ linkCopied ? $t('payment-method-ui.link-copied') : $t('payment-method-ui.copy-link') }}
          </v-btn>
          <v-btn
              small
            color="primary"
            @click="startAuthentication"
          >
            <v-icon left small>mdi-shield-check</v-icon>
            {{ $t('payment-method-ui.authenticate-now') }}
          </v-btn>
        </template>

        <!-- Error state actions -->
        <v-btn
            small
          v-if="error"
          text
          color="primary"
          @click="$emit('retry')"
        >
          {{ $t('payment-method-ui.retry-payment') }}
        </v-btn>

        <!-- Common close button -->
        <v-btn
            small
          v-if="error || success"
          text
          @click="close"
        >
          {{ $t('close') }}
        </v-btn>

        <!-- Success continue button -->
        <v-btn
            small
          v-if="success"
          color="success"
          @click="close"
        >
          {{ $t('continue') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import StripeService from '@service/StripeService'
import PaymentMethodRepository from '@repository/PaymentMethodRepository'

export default {
  name: 'PaymentAuthDialog',

  props: {
    value: {
      type: Boolean,
      default: false
    },
    paymentResponse: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      loading: false,
      success: false,
      error: false,
      errorMessage: '',
      paymentIntent: null,
      isRedirecting: false,
      loadingMessage: '',
      linkCopied: false,
      manualStart: false // Pour savoir si l'utilisateur a cliqué sur "S'authentifier maintenant"
    }
  },

  computed: {
    dialog: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    },

    statusIcon() {
      if (this.loading) {
        return { icon: 'mdi-loading mdi-spin', color: 'primary' }
      }
      if (this.success) {
        return { icon: 'mdi-check-circle', color: 'success' }
      }
      if (this.error) {
        return { icon: 'mdi-alert-circle', color: 'error' }
      }
      return { icon: 'mdi-shield-lock', color: 'primary' }
    },

    authenticationUrl() {
      // Si c'est une redirection, retourner l'URL de redirection
      if (this.paymentResponse?.next_action?.type === 'redirect_to_url') {
        return this.paymentResponse.next_action.redirect_to_url.url
      }
      // Pour les autres types, on pourrait générer une URL vers notre page de retour
      // avec le client_secret en paramètre (mais c'est sensible !)
      return null
    }
  },

  watch: {
    paymentResponse: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.handlePaymentResponse(newVal)
        }
      }
    }
  },

  methods: {
    async handlePaymentResponse(response) {
      // Réinitialiser les états
      this.success = false
      this.error = false
      this.errorMessage = ''
      this.paymentIntent = null
      this.manualStart = false

      // Vérifier si le paiement nécessite une authentification
      if (response.status === 'requires_action' && response.client_secret) {
        // Don't auto-start - just show the initial state with options
        // User will click "Authenticate now" to start
        return
      } else if (response.status === 'succeeded') {
        // Paiement réussi directement
        this.success = true
        this.paymentIntent = response
        this.$emit('success', response)
      } else if (response.status === 'processing') {
        // Paiement en cours de traitement
        this.success = true
        this.paymentIntent = response
        this.$emit('success', response)
      } else if (response.status === 'requires_capture') {
        // Préauthorisation réussie
        this.success = true
        this.paymentIntent = response
        this.$emit('success', response)
      } else {
        // Autre statut inattendu
        this.error = true
        this.errorMessage = `Unexpected payment status: ${response.status}`
        this.$emit('error', { message: this.errorMessage, response })
      }
    },

    async authenticatePayment(response) {
      this.loading = true
      this.loadingMessage = this.$t('payment-method-ui.authentication-in-progress')

      try {
        // Vérifier le type d'action
        const actionType = response.next_action?.type

        if (actionType === 'redirect_to_url') {
          this.isRedirecting = true
          this.loadingMessage = this.$t('payment-method-ui.redirecting-auth')
        }

        // Gérer l'authentification via Stripe
        const result = await StripeService.handleNextAction(response.client_secret)

        if (result.error) {
          // Erreur lors de l'authentification
          this.error = true
          this.errorMessage = result.error.message || this.$t('payment-method-ui.authentication-failed')
          this.$emit('error', result.error)
        } else if (result.paymentIntent) {
          // Authentification réussie, vérifier le statut
          await this.checkPaymentIntentStatus(result.paymentIntent)
        }
      } catch (err) {
        this.error = true
        this.errorMessage = err.message || this.$t('payment-method-ui.authentication-failed')
        this.$emit('error', err)
      } finally {
        this.loading = false
        this.isRedirecting = false
      }
    },

    async checkPaymentIntentStatus(paymentIntent) {
      this.paymentIntent = paymentIntent

      switch (paymentIntent.status) {
        case 'succeeded':
        case 'processing':
        case 'requires_capture':
          // Succès
          this.success = true
          this.$emit('success', paymentIntent)
          break

        case 'requires_payment_method':
          // Échec - carte invalide
          this.error = true
          this.errorMessage = this.$t('payment-method-ui.status.requires_payment_method')
          this.$emit('error', { message: this.errorMessage, paymentIntent })
          break

        case 'canceled':
          // Annulé
          this.error = true
          this.errorMessage = this.$t('payment-method-ui.authentication-canceled')
          this.$emit('error', { message: this.errorMessage, paymentIntent })
          break

        case 'failed':
          // Échec
          this.error = true
          this.errorMessage = this.$t('payment-method-ui.status.failed')
          this.$emit('error', { message: this.errorMessage, paymentIntent })
          break

        default:
          // Statut inattendu
          this.error = true
          this.errorMessage = `Unexpected status: ${paymentIntent.status}`
          this.$emit('error', { message: this.errorMessage, paymentIntent })
      }
    },

    statusColor(status) {
      const colors = {
        succeeded: 'green',
        processing: 'blue',
        requires_action: 'orange',
        requires_payment_method: 'orange',
        requires_capture: 'orange',
        requires_confirmation: 'orange',
        canceled: 'grey',
        failed: 'red'
      }
      return colors[status] || 'grey'
    },

    close() {
      this.dialog = false
      this.$emit('close')
    },

    startAuthentication() {
      // Set flag to indicate manual start
      this.manualStart = true
      // Call existing authenticatePayment method
      if (this.paymentResponse) {
        this.authenticatePayment(this.paymentResponse)
      }
    },

    async copyAuthLink() {
      try {
        // Si on a déjà une URL, la copier directement
        if (this.authenticationUrl) {
          await navigator.clipboard.writeText(this.authenticationUrl)
          this.linkCopied = true
          setTimeout(() => {
            this.linkCopied = false
          }, 2000)
          return
        }

        // Sinon, générer une URL de redirection en appelant le backend
        if (!this.paymentResponse?.id) {
          this.$dialog?.notify?.error && this.$dialog.notify.error(
            this.$t('payment-method-ui.no-payment-id')
          )
          return
        }

        this.loading = true
        this.loadingMessage = this.$t('payment-method-ui.generating-auth-link')

        // Construire l'URL de retour
        const returnUrl = `${window.location.origin}/payment/return?payment_intent=${this.paymentResponse.id}`

        // Appeler le backend pour générer l'URL
        const response = await PaymentMethodRepository.generateAuthUrl(
          this.paymentResponse.id,
          { returnUrl }
        )

        // Le backend retourne { authUrl, clientSecret, nextAction, status, paymentIntentId }
        const authUrl = response.authUrl

        if (authUrl) {
          // Copier l'URL générée
          await navigator.clipboard.writeText(authUrl)
          this.linkCopied = true
          setTimeout(() => {
            this.linkCopied = false
          }, 2000)
          this.$dialog?.notify?.success && this.$dialog.notify.success(
            this.$t('payment-method-ui.link-copied')
          )

          // Mettre à jour le paymentResponse pour afficher l'URL
          this.paymentResponse = {
            ...this.paymentResponse,
            client_secret: response.clientSecret,
            next_action: {
              type: 'redirect_to_url',
              redirect_to_url: {
                url: authUrl
              }
            }
          }
        } else {
          throw new Error('No authentication URL generated by backend')
        }
      } catch (err) {
        this.$dialog?.notify?.error && this.$dialog.notify.error(
          err?.response?.data?.message || err.message || this.$t('payment-method-ui.copy-link-failed')
        )
      } finally {
        this.loading = false
      }
    },

    sendAuthLinkToClient() {
      // Emit event for parent to handle
      this.$emit('send-link', {
        url: this.authenticationUrl,
        paymentIntent: this.paymentResponse
      })
    },

    formatAmount(amount, currency) {
      if (!amount) return '0.00'
      const amountInUnits = amount / 100
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: currency?.toUpperCase() || 'EUR'
      }).format(amountInUnits)
    }
  }
}
</script>

<style scoped>
.mdi-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
